/**
 * StatusSplitBar — a single 100%-stacked bar with a legend.
 *
 * Renders status distributions (visit approvals, store activity…) as coloured
 * proportional segments using the shared tone tokens.
 */

import React from 'react';
import {AppSmallText} from '../../../../Presentation/components/ui/Typography';
import type {StatusSegment} from '../models/homeAnalytics.model';
import {toneStyle} from '../utils/toneStyles';

interface StatusSplitBarProps {
  segments: StatusSegment[];
  emptyLabel: string;
}

const StatusSplitBar: React.FC<StatusSplitBarProps> = ({
  segments,
  emptyLabel
}) => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  if (total === 0) {
    return <AppSmallText className="text-gray-400">{emptyLabel}</AppSmallText>;
  }

  return (
    <div className="space-y-3">
      <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
        {segments
          .filter((segment) => segment.value > 0)
          .map((segment, index) => (
            <div
              key={`${segment.label}-${index}`}
              className={`${toneStyle(segment.tone).bg} transition-all duration-500`}
              style={{width: `${(segment.value / total) * 100}%`}}
              title={`${segment.label}: ${segment.value}`}
            />
          ))}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1">
        {segments.map((segment, index) => (
          <li
            key={`${segment.label}-${index}`}
            className="flex items-center gap-1.5"
          >
            <span
              className={`w-2.5 h-2.5 rounded-full ${toneStyle(segment.tone).bg}`}
            />
            <span className="text-xs text-grayText">{segment.label}</span>
            <span className="text-xs font-semibold text-grayText">
              {segment.value.toLocaleString('en-US')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default StatusSplitBar;
